import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, KeyRound, LogOut, ShieldCheck, UserPlus, Users as UsersIcon } from "lucide-react";
import {
  Badge, Button, Card, EmptyState, ErrorText, Input, Modal,
  PageHeader, Skeleton, Table, Td, Th, THead, Tr, useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { dateTime } from "../lib/format";

interface RoleSummary {
  id: string;
  key: string;
  nameAr: string;
  nameEn: string;
}

/** The shape /roles actually returns — a superset of the assignment-picker's RoleSummary. */
interface RoleListItem extends RoleSummary {
  isOwnerRole: boolean;
}

interface UserRow {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  isOwner: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  roles: RoleSummary[];
}

/**
 * Staff management.
 *
 * The owner row, when it appears at all, is read-only here by construction:
 * the server never returns it to a non-owner caller (404-masked, not merely
 * hidden), and even the owner viewing their own row is offered none of the
 * per-row actions — those are the same server-enforced refusals this screen
 * mirrors, never a UI-only restriction.
 */
export function UsersPage() {
  const { user: me, hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";

  const [items, setItems] = useState<UserRow[] | null>(null);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [form, setForm] = useState({ username: "", fullName: "", password: "", email: "", phone: "", roleIds: [] as string[] });

  const [rolesOpen, setRolesOpen] = useState<UserRow | null>(null);
  const [rolesSelection, setRolesSelection] = useState<string[]>([]);
  const [rolesBusy, setRolesBusy] = useState(false);

  const [resetOpen, setResetOpen] = useState<UserRow | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const canCreate = hasPermission("users.create");
  const canUpdate = hasPermission("users.update");
  const canDisable = hasPermission("users.disable");

  const load = useCallback(async () => {
    setError(null);
    try {
      const [u, r] = await Promise.all([
        apiFetch<{ items: UserRow[] }>("/users"),
        apiFetch<{ items: RoleListItem[] }>("/roles"),
      ]);
      setItems(u.items);
      // The owner role never appears as an assignable option — filtered here
      // too, but the real guarantee is the server's independent refusal.
      setRoles(r.items.filter((role) => !role.isOwnerRole));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : ar ? "تعذّر تحميل المستخدمين." : "Couldn't load users.");
    }
  }, [ar]);

  useEffect(() => { void load(); }, [load]);

  const resetCreateForm = () => setForm({ username: "", fullName: "", password: "", email: "", phone: "", roleIds: [] });

  const submitCreate = async () => {
    setCreateError(null);
    setCreateBusy(true);
    try {
      await apiFetch("/users", {
        method: "POST",
        body: {
          username: form.username.trim(),
          fullName: form.fullName.trim(),
          password: form.password,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          roleIds: form.roleIds,
        },
      });
      showToast("success", ar ? "أُنشئ المستخدم." : "User created.");
      setCreateOpen(false);
      resetCreateForm();
      await load();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : ar ? "تعذّر إنشاء المستخدم." : "Couldn't create the user.");
    } finally {
      setCreateBusy(false);
    }
  };

  const toggleActive = async (u: UserRow) => {
    setRowBusy(u.id);
    setRowError(null);
    try {
      await apiFetch(`/users/${u.id}/active`, { method: "POST", body: { active: !u.active } });
      showToast("success", u.active ? (ar ? "عُطِّل الحساب." : "Account deactivated.") : (ar ? "فُعِّل الحساب." : "Account activated."));
      await load();
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : ar ? "تعذّرت العملية." : "That didn't work.");
    } finally {
      setRowBusy(null);
    }
  };

  const forceLogout = async (u: UserRow) => {
    setRowBusy(u.id);
    setRowError(null);
    try {
      const res = await apiFetch<{ sessionsRevoked: number }>(`/users/${u.id}/force-logout`, { method: "POST", body: {} });
      showToast("success", ar ? `أُنهيت ${res.sessionsRevoked} جلسة.` : `${res.sessionsRevoked} session(s) ended.`);
    } catch (err) {
      setRowError(err instanceof ApiError ? err.message : ar ? "تعذّرت العملية." : "That didn't work.");
    } finally {
      setRowBusy(null);
    }
  };

  const openRoles = (u: UserRow) => {
    setRolesOpen(u);
    setRolesSelection(u.roles.map((r) => r.id));
  };

  const submitRoles = async () => {
    if (!rolesOpen) return;
    setRolesBusy(true);
    try {
      const res = await apiFetch<{ sessionsRevoked: number }>(`/users/${rolesOpen.id}/roles`, {
        method: "PUT", body: { roleIds: rolesSelection },
      });
      showToast("success",
        ar ? `حُدِّثت الأدوار${res.sessionsRevoked ? ` — أُنهيت ${res.sessionsRevoked} جلسة` : ""}.`
           : `Roles updated${res.sessionsRevoked ? ` — ${res.sessionsRevoked} session(s) ended` : ""}.`);
      setRolesOpen(null);
      await load();
    } catch (err) {
      showToast("error", err instanceof ApiError ? err.message : ar ? "تعذّر تحديث الأدوار." : "Couldn't update roles.");
    } finally {
      setRolesBusy(false);
    }
  };

  const submitReset = async () => {
    if (!resetOpen) return;
    setResetError(null);
    setResetBusy(true);
    try {
      await apiFetch(`/users/${resetOpen.id}/reset-password`, { method: "POST", body: { newPassword: resetPassword } });
      showToast("success", ar ? "أُعيد تعيين كلمة المرور." : "Password reset.");
      setResetOpen(null);
      setResetPassword("");
    } catch (err) {
      setResetError(err instanceof ApiError ? err.message : ar ? "تعذّرت إعادة التعيين." : "Couldn't reset the password.");
    } finally {
      setResetBusy(false);
    }
  };

  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "المستخدمون" : "Users"} />
        <Card>
          <EmptyState
            icon={<AlertTriangle className="size-6" />}
            title={ar ? "تعذّر التحميل" : "Couldn't load"}
            description={error}
            action={<Button size="sm" onClick={load}>{ar ? "إعادة المحاولة" : "Retry"}</Button>}
          />
        </Card>
      </div>
    );
  }

  if (!items) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-40" />
        <Card><Skeleton className="h-80 w-full" /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={ar ? "المستخدمون" : "Users"}
        subtitle={ar ? "فريق العمل وصلاحياته." : "Your team and their access."}
        trailing={canCreate && (
          <Button icon={<UserPlus className="size-4" />} onClick={() => { resetCreateForm(); setCreateError(null); setCreateOpen(true); }}>
            {ar ? "مستخدم جديد" : "New user"}
          </Button>
        )}
      />

      {rowError && <ErrorText>{rowError}</ErrorText>}

      {items.length === 0 ? (
        <Card>
          <EmptyState icon={<UsersIcon className="size-6" />} title={ar ? "لا يوجد مستخدمون" : "No users yet"} />
        </Card>
      ) : (
        <Card padding="none">
          <div className="p-5 sm:p-6">
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "الاسم" : "Name"}</Th>
                  <Th>{ar ? "اسم المستخدم" : "Username"}</Th>
                  <Th>{ar ? "الأدوار" : "Roles"}</Th>
                  <Th>{ar ? "الحالة" : "Status"}</Th>
                  <Th>{ar ? "آخر دخول" : "Last login"}</Th>
                  <Th>{ar ? "إجراءات" : "Actions"}</Th>
                </tr>
              </THead>
              <tbody>
                {items.map((u) => {
                  const isSelf = u.id === me?.id;
                  return (
                    <Tr key={u.id}>
                      <Td className="font-medium text-ink-900">
                        {u.fullName}
                        {u.isOwner && (
                          <span className="ms-1.5 inline-flex items-center gap-1 text-xs text-accent-600">
                            <ShieldCheck className="size-3" /> {ar ? "المالك" : "Owner"}
                          </span>
                        )}
                      </Td>
                      <Td className="tabular-nums text-muted-foreground">{u.username}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {u.roles.map((r) => (
                            <Badge key={r.id} tone="neutral">{ar ? r.nameAr : r.nameEn}</Badge>
                          ))}
                        </div>
                      </Td>
                      <Td>
                        <Badge tone={u.active ? "success" : "danger"}>
                          {u.active ? (ar ? "نشط" : "Active") : (ar ? "معطّل" : "Disabled")}
                        </Badge>
                      </Td>
                      <Td className="tabular-nums text-muted-foreground">
                        {u.lastLoginAt ? dateTime(u.lastLoginAt, language) : "—"}
                      </Td>
                      <Td>
                        {u.isOwner ? (
                          <span className="text-xs text-muted-subtle">{ar ? "غير قابل للتعديل" : "Not editable"}</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {canUpdate && (
                              <Button size="sm" variant="ghost" onClick={() => openRoles(u)}>
                                {ar ? "الأدوار" : "Roles"}
                              </Button>
                            )}
                            {canUpdate && (
                              <Button size="sm" variant="ghost" icon={<KeyRound className="size-3.5" />}
                                onClick={() => { setResetOpen(u); setResetPassword(""); setResetError(null); }}>
                                {ar ? "إعادة تعيين" : "Reset"}
                              </Button>
                            )}
                            {canUpdate && !isSelf && (
                              <Button size="sm" variant="ghost" icon={<LogOut className="size-3.5" />}
                                loading={rowBusy === u.id} onClick={() => forceLogout(u)}>
                                {ar ? "إنهاء الجلسات" : "Log out"}
                              </Button>
                            )}
                            {canDisable && !isSelf && (
                              <Button size="sm" variant={u.active ? "danger" : "outline"}
                                loading={rowBusy === u.id} onClick={() => toggleActive(u)}>
                                {u.active ? (ar ? "تعطيل" : "Disable") : (ar ? "تفعيل" : "Enable")}
                              </Button>
                            )}
                          </div>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* ── Create ──────────────────────────────────────────────────────── */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={ar ? "مستخدم جديد" : "New user"} closeLabel={ar ? "إغلاق" : "Close"}>
        <div className="space-y-3">
          <Input label={ar ? "الاسم الكامل" : "Full name"} value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} required />
          <Input label={ar ? "اسم المستخدم" : "Username"} value={form.username} onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} required />
          <Input label={ar ? "كلمة مرور مؤقتة" : "Temporary password"} type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required />
          <Input label={ar ? "البريد الإلكتروني" : "Email"} type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          <Input label={ar ? "الجوال" : "Mobile"} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          <div>
            <p className="mb-1.5 text-xs font-medium text-ink-700">{ar ? "الأدوار" : "Roles"}</p>
            <div className="space-y-1.5 rounded-lg border border-line p-2.5">
              {roles.map((r) => (
                <label key={r.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.roleIds.includes(r.id)}
                    onChange={(e) => setForm((f) => ({
                      ...f,
                      roleIds: e.target.checked ? [...f.roleIds, r.id] : f.roleIds.filter((id) => id !== r.id),
                    }))}
                    className="size-4 rounded border-line-strong text-accent-500"
                  />
                  {ar ? r.nameAr : r.nameEn}
                </label>
              ))}
            </div>
          </div>
          {createError && <ErrorText>{createError}</ErrorText>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setCreateOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
          <Button loading={createBusy} disabled={!form.username.trim() || !form.fullName.trim() || !form.password} onClick={submitCreate}>
            {ar ? "إنشاء" : "Create"}
          </Button>
        </div>
      </Modal>

      {/* ── Roles ───────────────────────────────────────────────────────── */}
      <Modal open={!!rolesOpen} onClose={() => setRolesOpen(null)} title={ar ? "تعديل الأدوار" : "Edit roles"} closeLabel={ar ? "إغلاق" : "Close"}>
        {rolesOpen && (
          <>
            <p className="text-sm text-ink-700">{rolesOpen.fullName}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {ar ? "يُنهي تغيير الأدوار جلسات هذا المستخدم الحالية." : "Changing roles ends this user's current sessions."}
            </p>
            <div className="mt-3 space-y-1.5 rounded-lg border border-line p-2.5">
              {roles.map((r) => (
                <label key={r.id} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={rolesSelection.includes(r.id)}
                    onChange={(e) => setRolesSelection((sel) => e.target.checked ? [...sel, r.id] : sel.filter((id) => id !== r.id))}
                    className="size-4 rounded border-line-strong text-accent-500"
                  />
                  {ar ? r.nameAr : r.nameEn}
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRolesOpen(null)}>{ar ? "إلغاء" : "Cancel"}</Button>
              <Button loading={rolesBusy} onClick={submitRoles}>{ar ? "حفظ" : "Save"}</Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Reset password ──────────────────────────────────────────────── */}
      <Modal open={!!resetOpen} onClose={() => setResetOpen(null)} title={ar ? "إعادة تعيين كلمة المرور" : "Reset password"} closeLabel={ar ? "إغلاق" : "Close"}>
        {resetOpen && (
          <>
            <p className="text-sm text-ink-700">{resetOpen.fullName}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {ar ? "سيُطلب من المستخدم تغييرها عند الدخول التالي." : "The user will be required to change it at next sign-in."}
            </p>
            <div className="mt-3">
              <Input label={ar ? "كلمة مرور مؤقتة جديدة" : "New temporary password"} type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} required />
            </div>
            {resetError && <div className="mt-3"><ErrorText>{resetError}</ErrorText></div>}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setResetOpen(null)}>{ar ? "إلغاء" : "Cancel"}</Button>
              <Button loading={resetBusy} disabled={resetPassword.length < 8} onClick={submitReset}>{ar ? "إعادة تعيين" : "Reset"}</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
